import { FontFamily } from '@euler/components/typography/fonts';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import React, { memo, useEffect, useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';

type Props = {
  children?: string;
};

const DOT_COUNT = 3;
const BOUNCE_HEIGHT = -6;
const DURATION = 350;
const STAGGER = 150;

const WavingDot = memo(
  ({ index, dotSize }: { index: number; dotSize: number }) => {
    const translateY = useSharedValue(0);

    useEffect(() => {
      const totalCycle = DURATION * 2 + STAGGER * (DOT_COUNT - 1) + 400;
      translateY.value = withDelay(
        index * STAGGER,
        withRepeat(
          withSequence(
            withTiming(BOUNCE_HEIGHT, {
              duration: DURATION,
              easing: Easing.out(Easing.ease),
            }),
            withTiming(0, {
              duration: DURATION,
              easing: Easing.in(Easing.ease),
            }),
            withDelay(
              totalCycle - DURATION * 2,
              withTiming(0, { duration: 0 }),
            ),
          ),
          -1,
        ),
      );
    }, [index, translateY]);

    const style = useAnimatedStyle(() => ({
      transform: [{ translateY: translateY.value }],
    }));

    return (
      <Animated.View
        style={[
          {
            width: dotSize,
            height: dotSize,
            borderRadius: dotSize / 2,
            backgroundColor: '#999',
            marginLeft: index > 0 ? 4 : 0,
          },
          style,
        ]}
      />
    );
  },
);

export const StreamingShimmer = memo(({ children = '思考中' }: Props) => {
  const { rpx } = useResponsiveContext();
  const dotSize = rpx(5);
  const s = useMemo(
    () =>
      StyleSheet.create({
        container: {
          flexDirection: 'row',
          alignItems: 'center',
          alignSelf: 'flex-start',
          minWidth: rpx(100),
        },
        label: {
          fontSize: rpx(14),
          fontFamily: FontFamily.NotoSans.Regular,
          color: '#999',
          flexShrink: 0,
        },
        dots: {
          flexDirection: 'row',
          alignItems: 'center',
          marginLeft: rpx(6),
          paddingTop: rpx(8),
          paddingBottom: rpx(2),
        },
      }),
    [rpx],
  );

  return (
    <View style={s.container}>
      <Text style={s.label} numberOfLines={1}>
        {children}
      </Text>
      <View style={s.dots}>
        {Array.from({ length: DOT_COUNT }, (_, i) => (
          <WavingDot key={i} index={i} dotSize={dotSize} />
        ))}
      </View>
    </View>
  );
});
